import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter, map, switchMap, take } from 'rxjs/operators';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { OrganisationUser, RequiredOrganisationUser } from '../models/auth-user';
import { ISignUpForm } from './authentication.service';
import { IAPIResponse } from './dashboard.service';
import { TimeZoneService } from './time-zone.service';
import { LoggedInUser, LoginResponseForAdmin, ManagerialOperationsService } from './managerial-operations.service';

export interface ILoginCredentials {
  email: string;
  password: string;
}
 
export interface IAuthResponse {
  statusCode:number;
  message: string;
  data?: any;
}
 
 
export class LoginResponseData {
  accessToken: string;
  refreshToken: string;
  userDetails: OrganisationUser | LoggedInUser | RequiredOrganisationUser;
  loggedInUser: any;

}
 
export class LoginResponse {
  statusCode: number;
  message:string;
  data: LoginResponseData;
}
 
@Injectable({
  providedIn: 'root'
})
export class UserAuthService {
 
  private url = `${environment.apiEndpoint}/api/v1/organisation-management/organisations`;
  
    private isTokenRefreshing = false;
    private newTokenSubject = new BehaviorSubject<string|null>(null);
  
  
  public get IsTokenRefreshing(){
    return this.isTokenRefreshing;
  }
  public get NewTokenSubject(){
    return this.newTokenSubject;
  }
  
  constructor(
    private httpService: HttpClient,
    private router: Router,
    private timeZoneService: TimeZoneService,
    private managerService: ManagerialOperationsService
  ) { }

    public getCurrenUser(): OrganisationUser | LoggedInUser | null {
      const userString = this.getLoggedInUser();
      if (userString == null) {
        return null;
      }
      try {
          const temp = JSON.parse(userString);

          // fullName and designationId are present if
          // user is manager or employee.
          // We handle this here after unifying login.
          if (temp.fullName && temp.designationId) {
              const loggedInUser = new LoggedInUser();
              loggedInUser.email = temp.email;
              loggedInUser.designationId = temp.designationId;
              loggedInUser.designationName = temp.designationName;
              loggedInUser.fullName = temp.fullName;
              loggedInUser.id = temp.id;
              loggedInUser.roleId = temp.roleId;
              loggedInUser.organisationCode = temp.organisationCode;
              loggedInUser.customTaskName = temp.customTaskName;
              loggedInUser.mobileOrg = temp.mobileOrg;
              loggedInUser.isRestricted = temp.isRestricted;
              return loggedInUser;
          }
          const loggedInUser = new OrganisationUser();
          loggedInUser.name = temp.name;
          loggedInUser.setupCompleted = temp.setupCompleted;
          loggedInUser.email = temp.email;
          loggedInUser.id = temp.id;
          loggedInUser.roleId = temp.roleId;
          loggedInUser.organisationCode = temp.organisationCode;
          loggedInUser.customTaskName = temp.customTaskName;
          loggedInUser.subscriptionPlanId = temp.subscriptionPlanId;
          loggedInUser.upgradePlanWarning = temp.upgradePlanWarning;
          loggedInUser.trialEndDate = temp.trialEndDate;
          loggedInUser.isInvoicePending = temp.isInvoicePending;
          loggedInUser.employeeId = temp.employeeId;
          loggedInUser.mobileOrg = temp.mobileOrg;
          loggedInUser.isRestricted = temp.isRestricted;
          return loggedInUser;
      } catch (e) {
          console.error(e);
          throw e;
      }
    }
    public getLoggedInUser(): string {
      const userDetails = sessionStorage.getItem('FocusRoAuthUserDetailsProvider');
      if (userDetails) {
        return userDetails;
      }
      return localStorage.getItem('FocusRoAuthUserDetailsProvider');
    }
    
    public storeLoggedInUser(data: LoginResponseData, rememberSession: boolean) {
      if (rememberSession) {
        localStorage.setItem('FocusRoAuthUserDetailsProvider', JSON.stringify(data.userDetails));
        localStorage.setItem('FocusRoAuthUserToken', data.accessToken);
      } else {
        sessionStorage.setItem('FocusRoAuthUserDetailsProvider', JSON.stringify(data.userDetails));
        sessionStorage.setItem('FocusRoAuthUserToken', data.accessToken);
      }
    }
    private authenticate(credentials: ILoginCredentials) {
      const reqUrl = `${this.url}/login`;
      return this.httpService.post<LoginResponse>(reqUrl, credentials);
    }
    private selectOrgToLogin(organisationCode, idToken: string) {
      const reqUrl = `${this.url}/select-org`;
      return this.httpService.post<LoginResponse>(reqUrl, organisationCode, { headers: new HttpHeaders({
        Authorization: `Bearer ${idToken}`,
    })});
    }
  /**
   * Signs in an user in to the app using the aws cognito service.
   * @param userEmail Email address the user uses to sign in
   * @param password Password the user uses to sign in
   */
  public login(credentials: ILoginCredentials, rememberSession: boolean) {
    return this.authenticate(credentials).pipe(
      map((response) => {
        if('requiresOrganisationSelection' in response.data.userDetails){
          return response;
        }
        else if((response.data.userDetails as LoggedInUser).designationId){
          const userDetails = response.data.userDetails as LoggedInUser;
          this.managerService.storeLoggedInUser(
              {
                  loggedInUser: {
                      customClientName: userDetails.customClientName,
                      customProjectName: userDetails.customProjectName,
                      customTaskName: userDetails.customTaskName,
                      designationId: userDetails.designationId,
                      designationName: userDetails.designationName,
                      email: userDetails.email,
                      fullName: userDetails.fullName,
                      id: userDetails.id,
                      mobileOrg: userDetails.mobileOrg,
                      isRestricted: userDetails.isRestricted,
                      organisationCode: userDetails.organisationCode,
                      orgTimezone: userDetails.orgTimezone,
                      roleId: userDetails.roleId,
                      setupCompleted: userDetails.setupCompleted,
                  },
                  refreshToken: response.data.refreshToken,
                  token: response.data.accessToken,
              },
              rememberSession
          );
          const intervel=this.managerService.getRefreshTokenIntervelForManager();
          // console.log("interval 5",intervel);
          setTimeout(()=>{
            this.managerService
                .refreshTokenForManager(userDetails.organisationCode)
                .subscribe();
          },intervel);
        }else{
          this.storeLoggedInUser(response.data, rememberSession);
          this.timeZoneService.setTimeZone(response.data.userDetails.orgTimezone);
          const intervel=this.getRefreshTokenIntervel();
          setTimeout(()=>{
            this.refreshToken().subscribe();
          },intervel);
        }
      })
    );
  }
  public getAuthToken(): string | null {
    const token = sessionStorage.getItem('FocusRoAuthUserToken');
    if (token) {
      return token;
    }
    return localStorage.getItem('FocusRoAuthUserToken');
  }
  public isLoggedIn() {
    if (this.getLoggedInUser()) {
      return true;
    }
    return false;
  }
  public logOut(userId: number,noRedirect?: boolean): void;
  public logOut(userId,noRedirect = false): void {
    const reqUrl = `${this.url}/logout`;
    const reqBody = {
                      userId : userId                     
                    };
    this.httpService.post<IAuthResponse>(reqUrl,reqBody)
      .subscribe((response) => {
        localStorage.removeItem('FocusRoAuthUserDetailsProvider');
        localStorage.removeItem('FocusRoAuthUserToken');
        sessionStorage.removeItem('FocusRoAuthUserDetailsProvider');
        sessionStorage.removeItem('FocusRoAuthUserToken');
        if (!noRedirect) {
          this.router.navigateByUrl('/auth/login');
 
        }
        this.clearCacheAndRedirect()
      },(error) => {
        console.log(error.error)
      });
  }
  private clearCacheAndRedirect(): void {
    window.location.reload();
  }
  public initPasswordReset(userEmail: string, organisationCode: string) {
    return new Observable<IAuthResponse>((observer) => {
      const reqUrl = `${this.url}/forget_password`;
      this.httpService.post<IAuthResponse>(reqUrl, {email:userEmail.trim(), organisationCode})
        .subscribe((response) => {
          observer.next(response);
        }, (error) => {
          observer.error({
            message: 'unable to send password reset code',
            error: error.error
          });
        });
    });
  }
 
  public changePassword(resetCode: number, newPassword: string, email: string) {
    return new Observable<IAuthResponse>((observer) => {
      const reqUrl = `${this.url}/reset_password`;
      const reqBody = {
                        password_reset_code : resetCode,
                        new_password : newPassword.trim(),
                        email : email.trim()
                      };
      this.httpService.post<IAuthResponse>(reqUrl,reqBody)
      .subscribe((response) => {
        observer.next(response);
      }, (error) => {
        observer.error({
          message: 'unable to change the password',
          error: error.error
        });
      });
    });
  }
  public signUp(request: ISignUpForm) {
    return new Observable((observer) => {
      const reqUrl = `${this.url}/register`;
      const reqBody = {
                        name : request.name,
                        organisationCode: request.nickName,
                        email : request.email,
                        password : request.password
                      };
      this.httpService.post<IAuthResponse>(reqUrl,reqBody)
      .subscribe((response) => {
        observer.next(response);
        },
          (error) => {
            observer.error({
              message: 'sign up failed',
              error: error.error
            });
          });
    });
  }
  public verifySignUp(userEmail: string, token: string) {
    return new Observable<IAuthResponse>((observer) => {
      const reqUrl = `${this.url}/verify_email`;
      const reqBody = {
                        email : userEmail.trim(),
                        verification_code : token                        
                      };
      this.httpService.post<IAuthResponse>(reqUrl,reqBody)
        .subscribe((response) => {
          observer.next(response);
        },(error) => {
          observer.error({
            message: 'Verification Failed',
            error: error.error
          });
        });
       
    });
 
  }
 
  public resendVerifcationCode(userEmail: string) {
    return new Observable<IAuthResponse>((observer) => {
      const reqUrl = `${this.url}/resend_code`;
      const reqBody = {
                        email : userEmail.trim()                    
                      };
      this.httpService.post<IAuthResponse>(reqUrl,reqBody)
        .subscribe((response) => {
          observer.next(response);
        },(error) => {
          observer.error({
            message: 'Unable to Resend Verification Code',
            error: error.error
          });
        })
    });
  }
  public refreshToken()
  {
    if (this.isTokenRefreshing) {
        return this.newTokenSubject.pipe(
            filter((token) => token !== null),
            take(1),
            switchMap(() => {
                return new Observable((observer) => {
                    let token: string = null;
                    if (localStorage.getItem('FocusRoAuthUserToken')) {
                        token = localStorage.getItem(
                            'FocusRoAuthUserToken'
                        );
                    } else if (
                        sessionStorage.getItem('FocusRoAuthUserToken')
                    ) {
                        token = sessionStorage.getItem(
                            'FocusRoAuthUserToken'
                        );
                    }
                    observer.next(token);
                    observer.complete();
                });
            })
        );
    }

    console.log(
        '%c🙇 Requesting new Access token%c',
        'color: white; background:rgb(78, 5, 91); padding: 2px 6px; border-radius: 4px; font-weight: bold;'
    );
    this.isTokenRefreshing = true;
    this.newTokenSubject.next(null);

    return  new Observable((observer) => {
    const reqUrl = `${this.url}/refreshToken`;
    this.httpService.post<IAuthResponse>(reqUrl, {})
        .subscribe((response) => {
            const accessToken= response.data.accessToken;
            if(localStorage.getItem("FocusRoAuthUserToken"))
            {
              localStorage.setItem('FocusRoAuthUserToken', accessToken);
            }
            else if(sessionStorage.getItem("FocusRoAuthUserToken"))
            {
              sessionStorage.setItem('FocusRoAuthUserToken',accessToken);
            }
            const intervel=this.getRefreshTokenIntervel();
            setTimeout(()=>{
              this.refreshToken().subscribe();
            },intervel);

            this.isTokenRefreshing = false;
            this.newTokenSubject.next(accessToken);
            observer.next(accessToken);
        }, (error) => {
            console.log(error.error);

            this.isTokenRefreshing = false;
            this.newTokenSubject.error(error.error);

            observer.error(error.error.message);
            const user= this.getCurrenUser();
            this.logOut(user.id);
        });
      }
    );
  }
  public checkTokenExpired(token: string)
  {
    const expiry = (JSON.parse(atob(token.split('.')[1]))).exp;
    return (Math.floor((new Date).getTime() / 1000)) >= expiry;
  }
  // public getTokenExpireTime(token: string)
  // {
  //   console.log('token:', token)
  //   console.log('token first split:', token.split('.'))
  //   console.log('token:', token.split('.')[1])
  //   console.log('token split atob:', (atob(token.split('.')[1])))
  //   console.log('JSON Parsed Token expire Time :', (JSON.parse(atob(token.split('.')[1]))).exp)
  //   const expire_time = (JSON.parse(atob(token.split('.')[1]))).exp;
  //   // const expire_time = (JSON.parse(window.atob(token.split('.')[1]))).exp;
  //   // const expire_time = (JSON.parse(window.atob(token.replace(/-/g, "+").replace(/_/g, "/")))).exp;
  //   console.log('exp', expire_time)
  //   return expire_time;
  // }
  public getTokenExpireTime(token: string) {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/').padEnd(base64Url.length + (4 - base64Url.length % 4) % 4, '=');
      const expire_time = (JSON.parse(atob(base64))).exp;
      return expire_time;
    } catch (error) {
      console.error("Error decoding token: ", error);
      // Handle the error accordingly
      return null;
    }
  }
  public getRefreshTokenIntervel()
  {
    const currentTime = Date.now();
    const token = this.getAuthToken();  
    const expireTime = this.getTokenExpireTime(token)*1000;
    const time = expireTime - currentTime -(60*1000);//refresh token one minut before expire;
    return time;
  }

  public verifyDemoToken(orgCode: string, token: string) {
    return new Observable<IAuthResponse>((observer) => {
        const reqUrl = `${this.url}/${orgCode}/verify-demo-token`;
        const reqBody = {
            loginToken: token,
        };
        this.httpService.post<IAuthResponse>(reqUrl, reqBody).subscribe(
            (response) => {
                this.timeZoneService.setTimeZone(
                  response.data.userDetails.orgTimezone
                );
                observer.next(response);
            },
            (error) => {
                observer.error({
                    message: 'Verification Failed',
                    error: error.error,
                });
            }
        );
    });
  }

  public verifyLoginToken(orgCode: string, token: string, type: string) {
    return new Observable<IAuthResponse>((observer) => {
      const reqUrl = `${this.url}/${orgCode}/verify-login-token`;
      const reqBody = {
                        loginToken : token,
                        loginType: type == 'demo'? 'demo': 'normal'                     
                      };
      this.httpService.post<IAuthResponse>(reqUrl,reqBody)
        .subscribe((response) => {
          if(type == 'org' || type == 'demo'){
            this.timeZoneService.setTimeZone(response.data.userDetails.orgTimezone);
          }else{
            this.timeZoneService.setTimeZone(response.data.loggedInUser.orgTimezone);
          }
          observer.next(response);
        },(error) => {
          observer.error({
            message: 'Verification Failed',
            error: error.error
          });
        });
       
    });
  }
 
  public getPabblyLoginUrl(orgCode: string) {
    return this.httpService.get<IAPIResponse<IPabblyLoginResp>>(`${this.url}/${orgCode}/pabbly-login`);
  }
 
  public getStripeLoginUrl(orgCode: string) {
    return this.httpService.get<IAPIResponse<IStripeLoginResp>>(`${this.url}/${orgCode}/invoice-payement-link`);
  }
 
  public getStripeCustomerPortal(orgCode: string) {
    return this.httpService.get<IAPIResponse<IStripeLoginResp>>(`${this.url}/${orgCode}/stripe-customer-portal`);
  }
 
  public getStripeDetails(orgCode: string) {
    return this.httpService.get<IAPIResponse<IOrganisationDetailsResp>>(`${this.url}/${orgCode}/get-stripe-details`);
  }
  public getOrgTypeDetails(orgCode: string) {
    return this.httpService.get<IAPIResponse<IOrganisationTypeResp>>(`${this.url}/${orgCode}/get-org-type`);
  }
public loginWithSelectedUser(
  organisationCode: any,
  rememberSession: boolean,
  token: string,
) {
  return this.selectOrgToLogin(organisationCode, token).pipe(
    map((response: LoginResponse | LoginResponseForAdmin) => {
      const userDetails = response.data.userDetails as any;

      const isEmployeeLogin = 'designationId' in userDetails;

      if (isEmployeeLogin) {
        // Handle Employee login
        const employeeUser = userDetails as LoggedInUser;

        this.managerService.storeLoggedInUser(
          {
            loggedInUser: {
              id: employeeUser.id,
              email: employeeUser.email,
              fullName: employeeUser.fullName,
              designationId: employeeUser.designationId,
              designationName: employeeUser.designationName,
              organisationCode: employeeUser.organisationCode,
              roleId: employeeUser.roleId,
              customTaskName: employeeUser.customTaskName,
              customClientName: employeeUser.customClientName,
              customProjectName: employeeUser.customProjectName,
              orgTimezone: employeeUser.orgTimezone,
              mobileOrg: employeeUser.mobileOrg,
              isRestricted: employeeUser.isRestricted,
              setupCompleted: employeeUser.setupCompleted,
            },
            refreshToken: response.data.refreshToken,
            token: response.data.accessToken,
          },
          rememberSession
        );

        const interval = this.managerService.getRefreshTokenIntervelForManager();
        setTimeout(() => {
          this.managerService
            .refreshTokenForManager(employeeUser.organisationCode)
            .subscribe();
        }, interval);

      } else {
        // Handle Admin login
        const adminUser = userDetails as OrganisationUser;

        this.storeLoggedInUser(
            { ...response.data, loggedInUser: null },
            rememberSession
        );

        const intervel = this.getRefreshTokenIntervel();
        setTimeout(() => {
            this.refreshToken().subscribe();
        }, intervel);
      }
    })
  );
}


  
}
 
export interface IPabblyLoginResp{
  token: string;
  access_url: string;
  expires_at: Date;
}
 
export interface IStripeLoginResp{  
  access_url: string;  
}
 
export interface IOrganisationDetailsResp{
  stripeDetails: any;  
}
export interface IOrganisationTypeResp{
  liveStreamingModule?: number;
  isMobileOrg: number;  
  planId?:number;
  imageDownloadHost?:string;
  allowPayrollOption?:number;
  isPayroll?:number;
  isBiometricEnabled?: boolean | number;
}